import { ChevronDown } from "lucide-react"
import { useState } from "react"
import { JsonView } from "@/components/json-view"
import type { Artifact } from "@/lib/defs"
import { useT } from "@/lib/i18n"
import { asString, isRecord } from "@/lib/utils"

export interface Origin {
  readonly kind: "imported" | "authored" | "generated" | "recorded"
  readonly where: string
  readonly detail?: string
  /** Norn property → source field, when an importer recorded the correspondence. */
  readonly members: readonly [string, string][]
}

/** What the cell actually retained about where a definition came from; null when it retained nothing. */
export function definitionOrigin(artifact: Artifact): Origin | null {
  const provenance = isRecord(artifact.raw.provenance) ? artifact.raw.provenance : null
  const extensions = isRecord(artifact.payload.extensions) ? artifact.payload.extensions : null
  const importer = extensions ? Object.entries(extensions).find(([k]) => k.startsWith("norn.importers.")) : undefined
  if (importer && isRecord(importer[1])) {
    const ext = importer[1]
    const app = isRecord(ext.app) ? ext.app : {}
    const members = isRecord(ext.members) ? Object.entries(ext.members).flatMap(([k, v]) => (typeof v === "string" ? [[k, v] as [string, string]] : [])) : []
    const doctype = asString(ext.doctype) ?? asString(ext.source) ?? artifact.artifactId
    return {
      kind: "imported",
      where: `${asString(app.sourceSystem) ?? asString(app.namespace) ?? ""} · ${doctype}`.replace(/^ · /, ""),
      detail: [importer[0].replace(/^norn\.importers\./, ""), asString(ext.module)].filter(Boolean).join(" · "),
      members,
    }
  }
  const path = provenance ? asString(provenance.sourcePath) : undefined
  const source = provenance ? asString(provenance.source) : undefined
  if (!path && !source) return null
  if (path && /\.(json|ts|tsx|star|toml|ya?ml)$/.test(path)) return { kind: "authored", where: path, detail: source, members: [] }
  if (path && /^norn-/.test(path)) return { kind: "generated", where: path, detail: source, members: [] }
  return { kind: "recorded", where: path ?? source ?? "", detail: path ? source : undefined, members: [] }
}

/** The definition's origin as the cell recorded it, with the compiled IR folded away. */
export function DefinitionSource({ artifact, origin }: { artifact: Artifact; origin: Origin }) {
  const t = useT()
  const [irOpen, setIrOpen] = useState(false)
  const heading = {
    imported: t("dv.source.imported"),
    authored: t("dv.source.authored"),
    generated: t("dv.source.generated"),
    recorded: t("dv.source.recorded"),
  }[origin.kind]
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-dashed border-[var(--lineage)]/50 px-5 py-4">
        <p className="text-xs text-[var(--lineage)]">{heading}</p>
        <p dir="ltr" className="mt-1 font-mono text-sm">
          {origin.where}
        </p>
        {origin.detail ? <p className="mt-1 text-xs text-muted-foreground">{origin.detail}</p> : null}
      </div>
      {origin.members.length ? (
        <section className="space-y-2">
          <h3 className="text-[15px] font-semibold tracking-tight">{t("dv.source.members")}</h3>
          <div className="overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[520px] text-sm">
              <thead className="text-[11px] text-muted-foreground uppercase tracking-[0.06em]">
                <tr className="border-b border-border">
                  <th className="px-4 py-2.5 text-start font-medium">{t("dv.source.sourceField")}</th>
                  <th className="px-4 py-2.5 text-start font-medium">{t("dv.source.nornProperty")}</th>
                </tr>
              </thead>
              <tbody>
                {origin.members.map(([prop, field]) => (
                  <tr key={prop} className="border-b border-border/60 last:border-0">
                    <td dir="ltr" className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {field}
                    </td>
                    <td dir="ltr" className="px-4 py-2 font-mono text-xs">
                      {prop}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      <div>
        <button type="button" aria-expanded={irOpen} onClick={() => setIrOpen((v) => !v)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ChevronDown className={irOpen ? "size-4" : "size-4 -rotate-90 rtl:rotate-90"} aria-hidden />
          {t("dv.source.compiled")}
        </button>
        {irOpen ? <JsonView value={artifact.raw} className="mt-3 max-h-[60vh]" /> : null}
      </div>
    </div>
  )
}
